import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPopular } from './PopularItems';
import { vendors } from '../constants';

/**
 * Hook for searching, filtering and sorting vendors with pagination
 */
export const useVendorFilter = () => {

    const navigate = useNavigate();
    const { search } = useParams();

    // State variables
    const [matchingVendors, setMatchingVendors] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOption, setSortOption] = useState('');
    const [filters, setFilters] = useState({
        category: '',
        location: '',
        minPrice: 0,
        maxPrice: Infinity,
        availability: ''
    });

    // Items per page
    const itemsPerPage = 8;

    // Synchronize search keyword with URL params
    useEffect(() => {
        setSearchKeyword(search || '');
        setCurrentPage(1); // Reset to first page on new search
    }, [search]);

    // Filter and sort vendors whenever search keyword, filters or sort option changes
    useEffect(() => {
        let filteredVendors = searchKeyword 
            ? handleHeaderSearch(searchKeyword)
            : [...vendors];

        // Apply additional filters
        filteredVendors = applyVendorFilters(filteredVendors, filters);

        // Apply sorting
        filteredVendors = handleSortChange(filteredVendors, sortOption);

        setMatchingVendors(filteredVendors);
    }, [searchKeyword, sortOption, filters]);

    // Filters vendors based on a search keyword
    const handleHeaderSearch = (searchTerm) => {
        const searchLower = searchTerm.toLowerCase();

        // Check if search term is a number (price search)
        const isNumericSearch = !isNaN(searchTerm.replace(/[^0-9]/g, '')) && 
                              searchTerm.replace(/[^0-9]/g, '').length > 0;
        const numericValue = isNumericSearch ? parseInt(searchTerm.replace(/[^0-9]/g, '')) : null;

        return vendors.filter((vendor) => {
            // Regular text matches
            const textMatch = 
                vendor.title?.toLowerCase().includes(searchLower) ||
                vendor.details.category?.toLowerCase().includes(searchLower) ||
                vendor.details.location?.toLowerCase().includes(searchLower) ||
                vendor.details.availability?.toLowerCase().includes(searchLower);
    
            // If numeric search, check price range
            if (isNumericSearch) {
                const priceRange = vendor.details.priceRange.match(/\$?([\d,]+)/g);
                if (priceRange) {
                    const minPrice = parseInt(priceRange[0].replace(/[^\d]/g, ''));
                    const maxPrice = priceRange[1] 
                        ? parseInt(priceRange[1].replace(/[^\d]/g, '')) 
                        : minPrice;
                    
                    // Check if search number falls within vendor's price range
                    const priceMatch = numericValue >= minPrice && numericValue <= maxPrice;
                    return textMatch || priceMatch;
                }
            }
    
            return textMatch;
        });
    };

    // Handles header search form submission
    const handleHeaderSearchSubmit = (searchQuery) => {
        const encodedQuery = encodeURIComponent(searchQuery);
        navigate(`/Vendors/Search/${encodedQuery}`);
    };

    /**
     * Applies vendor-specific filters to vendors
     * @param {Array} vendors - The vendors to filter
     * @param {Object} filters - The filters to apply
     * @returns {Array} - Filtered vendors
     */
    const applyVendorFilters = (vendors, filters) => {

        return vendors.filter(vendor => {

            // Category filter
            if (filters.category && vendor.details.category !== filters.category) {
                return false;
            }

            // Location filter
            if (filters.location && !vendor.details.location.includes(filters.location)) {
                return false;
            }

            // Price range filter
            if (filters.minPrice > 0 || filters.maxPrice < Infinity) {
                const priceRange = vendor.details.priceRange.match(/\$?([\d,]+)/g);
                if (priceRange && priceRange.length >= 1) {
                    // Extract min and max prices from the price range string
                    const minVendorPrice = parseInt(priceRange[0].replace(/[^\d]/g, ''));
                    const maxVendorPrice = priceRange[1] 
                        ? parseInt(priceRange[1].replace(/[^\d]/g, '')) 
                        : minVendorPrice; // If only one price, use it for both min and max
                    
                    // Check if vendor's price range overlaps with filter range
                    if (minVendorPrice > filters.maxPrice || maxVendorPrice < filters.minPrice) {
                        return false;
                    }
                } else {
                    // If no price range can be extracted, exclude if price filtering is active
                    if (filters.minPrice > 0 || filters.maxPrice < Infinity) {
                        return false;
                    }
                }
            }

            // Availability filter
            if (filters.availability && !vendor.details.availability.includes(filters.availability)) {
                return false;
            }

            return true;

        });
    };

    /**
     * Updates the filters state
     * @param {Object} newFilters - The new filters to apply
     */
    const handleFilterChange = (newFilters) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters
        }));
        setCurrentPage(1); // Reset to first page on filter change
    };

    /**
     * Sorts vendors based on the selected option
     * @param {Array} vendors - The vendors to sort
     * @param {string} sortOption - The sorting option
     * @returns {Array} - Sorted vendors
     */
    const handleSortChange = (vendors, sortOption) => {
        switch (sortOption) {

            case 'featured':
                return getPopular(vendors);

            case 'lowPrice':
                return [...vendors].sort((a, b) => {
                    const getLowPrice = (priceRange) => {
                        const match = priceRange.match(/\$?([\d,]+)/); // Get the first number
                        return match ? parseInt(match[1].replace(/,/g, '')) : 0;
                    };
                    const aPrice = getLowPrice(a.details.priceRange);
                    const bPrice = getLowPrice(b.details.priceRange);
                    return aPrice - bPrice;
                });

            case 'highPrice':
                return [...vendors].sort((a, b) => {
                    const getHighPrice = (priceRange) => {
                        const match = priceRange.match(/\$?([\d,]+)/); // Get the first number
                        return match ? parseInt(match[1].replace(/,/g, '')) : 0;
                    };
                    const aPrice = getHighPrice(a.details.priceRange);
                    const bPrice = getHighPrice(b.details.priceRange);
                    return bPrice - aPrice;
                });

            default:
                return vendors;

        }
    };

    /**
     * Handles sorting option change
     * @param {Object} selectedOption - The selected sorting option
     */
    const handleSort = (selectedOption) => {
        setSortOption(selectedOption ? selectedOption.value : '');
        setCurrentPage(1); // Reset to first page on sort change
    };

    // Pagination calculations
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedVendors = matchingVendors.slice(startIndex, endIndex);
    const totalPages = Math.ceil(matchingVendors.length / itemsPerPage);

    // Function to handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0); // Optional: scroll to top on page change
    };

    return {
        searchKeyword,
        matchingVendors,
        displayedVendors,
        currentPage,
        totalPages,
        sortOption,
        filters,
        handlePageChange,
        handleHeaderSearchSubmit,
        handleSort,
        handleFilterChange,
    };

};